import * as _ from 'underscore';
import { UIElement } from './uiElement';
import { List, AutoCompleteUtil } from '../..';
import { DataSource } from '../../data';
import { IKeyValue } from '../../../interfaces';
import {
	AutoCompleteOptions,
	PreSortingOptions,
	FilterOptions,
	PagingOptions,
	AutoCompleteOperatorOptions,
	ListOptions,
	DataSourceOptions
} from '../../../interfaces/options';
import {
	AutoCompleteOperator,
	SortState,
	PropertyType,
	SelectionMode,
	FilterOperator,
	KeyCode
} from '../../../enums';
import { ID } from '../../../const';
import { DefaultAutoCompleteOptions } from '../../../const/defaultOptions/index';

export class AutoComplete<T> extends UIElement<T> {
	public list: List<T>;
	private textInput: KnockoutObservable<string>;
	private textInputThrottled: KnockoutComputed<string>;
	private keyUp: (dom: any, event: JQueryKeyEventObject) => void;
	private filterKey: string;
	private clickSelect: (item: T) => void;
	private isListHover: KnockoutObservable<boolean>;
	private listHover: (over: boolean) => void;
	private listVisible: KnockoutComputed<boolean>;
	private showList: boolean;
	constructor(
		id: string,
		label: string,
		value: T | KnockoutObservable<T>,
		options: AutoCompleteOptions<T>
	) {
		options = $.extend(true, {}, DefaultAutoCompleteOptions, options || {});
		super(id, label, value, options.uiElement);
		this.filterKey = options.filterKey;
		this.showList = options.showList;
		this.textInput = ko.observable<string>('');
		this.textInputThrottled = ko.computed<string>(this.textInput);
		if (_.isNumber(options.delay) && options.delay > 0) {
			this.textInputThrottled.extend({ throttle: options.delay });
		}
		if (options.list instanceof List) {
			this.list = options.list;
		} else {
			var preSorting: PreSortingOptions<T> = <PreSortingOptions<T>>{
				key: options.filterKey,
				fn: (value: string) => {
					let textInput = this.textInput();
					let points = 0;
					for (let i = 0; i < options.comparer.length; i++) {
						let comparer: AutoCompleteOperator | AutoCompleteOperatorOptions = options.comparer[i];
						if (AutoCompleteUtil.isAutoCompleteOperator(comparer)) {
							points += AutoCompleteUtil.fn[comparer](value, textInput);
						} else if (AutoCompleteUtil.isAutoCompleteOperatorOptions(comparer)) {
							points += AutoCompleteUtil.fn[comparer.operator](value, textInput, comparer.factor);
						}
					}
					return points;
				},
				state: SortState.ASC
			};
			var preFilter: FilterOptions = <FilterOptions>{
				key: options.filterKey,
				params: [this.textInputThrottled],
				operator: FilterOperator.LIKE,
				type: PropertyType.STRING
			};
			options.list = $.extend(
				true,
				{},
				<ListOptions<T>>{
					selectionMode: SelectionMode.KEEPLAST,
					dataSource: {
						filter: {
							prefilter: []
						},
						sorting: {
							preSorting: []
						},
						paging: <PagingOptions>{
							pageSize: 7
						}
					}
				},
				options.list || {}
			);
			if (!options.serverSort) {
				(<DataSourceOptions<T>>options.list.dataSource).sorting.preSorting.push(preSorting);
			}
			if (!options.severFilter) {
				(<DataSourceOptions<T>>options.list.dataSource).filter.prefilter.push(preFilter);
			}
			this.list = new List(<ListOptions<T>>options.list);
		}
		if (options.severFilter) {
			this.textInputThrottled.subscribe((newValue: string) => {
				this.list.reload(newValue);
			});
		}
		let _value: T;
		if ((_value = ko.unwrap(value))) {
			this.list.toggleSelection(_value);
		}
		this.keyUp = (dom: any, event: JQueryKeyEventObject): void => {
			switch (event.keyCode) {
				case KeyCode.DOWN_ARROW:
					this.selectNext();
					break;
				case KeyCode.UP_ARROW:
					this.selectPrevious();
					break;
				case KeyCode.ENTER:
					this.persistSelection();
					break;
				case KeyCode.ESCAPE:
					this.clearSelection();
					break;
			}
		};
		let focusSubscription = (newFocus: boolean) => {
			if (!newFocus && !this.isListHover()) {
				let textInput = this.textInput();
				let value = this.value();
				this.clearSelection();
				if (!_.isEmpty(textInput) && (_.isEmpty(value) || textInput !== value[this.filterKey])) {
					if (this.selectBy(textInput) == undefined) {
						if (!_.isEmpty(value)) {
							this.textInput(value[this.filterKey]);
						} else {
							this.textInput('');
						}
					} else {
						this.persistSelection();
					}
				} else if (!_.isEmpty(textInput) && !_.isEmpty(value)) {
					this.textInput(value[this.filterKey]);
				} else {
					this.value(null);
					this.textInput('');
				}
			}
		};
		this.hasFocus.subscribe(focusSubscription);
		this.clickSelect = (item: T): void => {
			if (item && item != this.value()) {
				this.list.toggleSelection(item);
				this.persistSelection();
			}
		};
		this.isListHover = ko.observable<boolean>(false);
		this.listHover = (over: boolean): void => {
			this.isListHover(over);
			if (!over && !this.hasFocus()) {
				focusSubscription(false);
			}
		};
		this.listVisible = ko.pureComputed<boolean>(() => {
			return this.showList && (this.hasFocus() || this.isListHover());
		});
	}
	private persistSelection() {
		let selectedItem = this.getSelectedItem();
		if (selectedItem) {
			this.value(selectedItem);
			this.textInput(selectedItem[this.filterKey]);
			this.hasFocus(false);
		}
	}
	public selectPrevious() {
		let view: T[];
		if ((view = this.list.view()).length > 0) {
			let selected = this.getSelectedItem();
			let index = view.length - 1;
			if (selected) {
				index = Math.max(view.indexOf(selected) - 1, 0);
			}
			this.list.toggleSelection(view[index]);
		}
	}
	public selectNext() {
		let view: T[];
		if ((view = this.list.view()).length > 0) {
			let selected = this.getSelectedItem();
			let index = 0;
			if (selected) {
				index = Math.min(view.indexOf(selected) + 1, view.length - 1);
			}
			this.list.toggleSelection(view[index]);
		}
	}
	public getSelectedItem(): T {
		return this.list.selectedItems[0];
	}
	public selectItem(item: T | string, property?: string): T {
		let selectedItem: T;
		if ((selectedItem = this.list.selectItem(item, property))) {
			this.persistSelection();
		}
		return selectedItem;
	}
	public selectBy(value: any, property: string = ID.ID): T {
		let selectedItem: T;
		if ((selectedItem = this.list.selectBy(value, property))) {
			this.persistSelection();
		}
		return selectedItem;
	}
	public clearSelection(): void {
		this.list.clearSelection();
	}
	//overwrite
	public serialize(serializeObject: IKeyValue<any>): IKeyValue<any> {
		serializeObject[this.id] = this.getSelectedItem();
		return serializeObject;
	}
	public reload(): Promise<DataSource<T>> {
		this.value(null);
		return this.list.reload(this.textInputThrottled());
	}
}
